import { cn } from '@workspace/ui/lib/utils'
import { Loader2, Upload } from 'lucide-react'
import type { ReactNode } from 'react'

export interface DropzoneProps {
  title: string
  /** The quiet line under the title: what is accepted, and how big. */
  hint: string
  /** Drag state, owned by whatever drop library the caller uses. */
  isDragActive?: boolean
  isUploading?: boolean
  /** Shown in place of the hint when something went wrong. */
  error?: string | null
  /** Spread the drop library's root props here. */
  rootProps?: Record<string, unknown>
  /** The file input the drop library expects to exist. */
  children?: ReactNode
  className?: string
}

/**
 * The "give us a file" target: a dashed panel with an icon, a title and a hint.
 *
 * Pure UI — no dropzone library, no upload logic, no browser APIs — so the
 * caller owns drag handling and this stays usable anywhere. It exists because
 * the Home wizard's first card and the feedback page both need the same target,
 * and the alternative was a second hand-rolled panel that drifted from the
 * first.
 */
export function Dropzone({
  title,
  hint,
  isDragActive = false,
  isUploading = false,
  error,
  rootProps,
  children,
  className,
}: DropzoneProps) {
  return (
    <div
      {...rootProps}
      className={cn(
        'rounded-xl border border-dashed px-6 py-10 text-center transition-colors',
        isDragActive ? 'border-primary/60 bg-primary/5' : 'border-border/60 bg-card/30',
        isUploading && 'opacity-70',
        className
      )}
    >
      {children}

      <div className="mx-auto flex size-11 items-center justify-center rounded-xl bg-primary/10">
        {isUploading ? (
          <Loader2 className="size-5 animate-spin text-primary" />
        ) : (
          <Upload className="size-5 text-primary" />
        )}
      </div>

      <div className="mt-4 text-[15px] font-semibold text-foreground">
        {isUploading ? 'Uploading your deck…' : title}
      </div>

      <div
        className={cn(
          'mt-1 text-[13px]',
          // The error replaces the hint rather than stacking under it: the
          // founder needs one instruction at a time, and the hint is what just
          // failed to be followed.
          error ? 'text-red-400' : 'text-muted-foreground'
        )}
      >
        {error ?? hint}
      </div>
    </div>
  )
}
